import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, onSnapshot, orderBy, limit } from 'firebase/firestore';
import { db } from '../../services/firebase';
import {
  TruckIcon,
  UsersIcon,
  BanknotesIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';



const isToday = (dateString) => {
  if (!dateString) return false;
  const date = dateString.toDate ? dateString.toDate() : new Date(dateString);
  const today = new Date();
  return date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();
};



// Stat Card
function StatCard({ title, value, icon: Icon, trend, trendValue, color, bgColor }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-all">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-gray-500 text-sm font-medium mb-1">{title}</p>
          <p className="text-3xl font-bold text-gray-900">{value}</p>
          {trend && (
            <div className={`flex items-center gap-1 mt-2 ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
              <ArrowTrendingUpIcon className={`w-4 h-4 ${trend === 'down' && 'rotate-180'}`} />
              <span className="text-sm font-medium">{trendValue}</span>
            </div>
          )}
        </div>
        <div className={`w-12 h-12 ${bgColor} rounded-xl flex items-center justify-center`}>
          <Icon className={`w-6 h-6 ${color}`} />
        </div>
      </div>
    </div>
  );
}

// Recent Task Card
function RecentTaskCard({ task }) {
  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-700',
      accepted: 'bg-blue-100 text-blue-700',
      picked_up: 'bg-purple-100 text-purple-700',
      in_transit: 'bg-indigo-100 text-indigo-700',
      delivered: 'bg-green-100 text-green-700',
      failed: 'bg-red-100 text-red-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getPaymentColor = (mode) => {
    const colors = {
      cash: 'text-yellow-700',
      online: 'text-blue-700',
      prepaid: 'text-green-700',
    };
    return colors[mode] || 'text-gray-700';
  };

  return (
    <div className="bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-all">
      <div className="flex items-center justify-between mb-3">
        <span className="text-red-700 font-semibold text-sm">{task.invoiceNumber}</span>
        <span className={`px-2 py-1 rounded-lg text-xs font-medium ${getStatusColor(task.status)}`}>
          {task.status?.replace('_', ' ').toUpperCase()}
        </span>
      </div>
      <p className="text-gray-900 font-medium mb-1">{task.customer?.name}</p>
      <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
        <MapPinIcon className="w-4 h-4" />
        <span className="truncate">{task.delivery?.address}</span>
      </div>
      <div className="flex items-center justify-between">
        <span className={`font-bold ${getPaymentColor(task.payment?.mode)}`}>
          ₹{task.payment?.amount?.toLocaleString('en-IN')}
        </span>
        <span className="text-gray-500 text-xs">{task.payment?.mode?.toUpperCase()}</span>
      </div>
    </div>
  );
}

// Porter Status Card
function PorterStatusCard({ porter }) {
  return (
    <div className="flex items-center gap-4 bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-all">
      <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
        <UsersIcon className="w-6 h-6 text-gray-600" />
      </div>
      <div className="flex-1">
        <p className="text-gray-900 font-medium">{porter.name}</p>
        <p className="text-gray-500 text-sm">{porter.employeeId}</p>
      </div>
      <div className="text-right">
        <div className={`flex items-center gap-2 ${porter.isOnline ? 'text-green-600' : 'text-gray-500'}`}>
          <div className={`w-2 h-2 rounded-full ${porter.isOnline ? 'bg-green-600' : 'bg-gray-500'}`}></div>
          <span className="text-sm font-medium">{porter.isOnline ? 'Online' : 'Offline'}</span>
        </div>
        {porter.stats?.pendingDeposits > 0 && (
          <p className="text-yellow-700 text-xs mt-1">
            ₹{porter.stats.pendingDeposits.toLocaleString('en-IN')} pending
          </p>
        )}
      </div>
    </div>
  );
}

function SparesDashboard() {
  const [stats, setStats] = useState({
    totalDeliveries: 0,
    pendingDeliveries: 0,
    totalPorters: 0,
    onlinePorters: 0,
    todayCollections: 0,
    pendingDeposits: 0,
  });
  const [recentTasks, setRecentTasks] = useState([]);
  const [porters, setPorters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const tasksQuery = query(
      collection(db, 'tasks'),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    const unsubscribeTasks = onSnapshot(tasksQuery, (snapshot) => {
      const tasks = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setRecentTasks(tasks.slice(0, 5));

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const pendingDeliveries = tasks.filter(t => 
        ['pending', 'accepted', 'picked_up', 'in_transit'].includes(t.status)
      ).length;

   // Then in your stats calculation, use:
const todayDeliveries = tasks.filter(t => 
  t.status === 'delivered' && isToday(t.deliveredAt || t.updatedAt)
).length;

const todayCollections = tasks
  .filter(t => t.status === 'delivered' && isToday(t.deliveredAt || t.updatedAt) && t.payment?.collected)
  .reduce((sum, t) => sum + (t.payment?.collectedAmount || t.payment?.amount || 0), 0);

      setStats(prev => ({
        ...prev,
        totalDeliveries: snapshot.size,
        pendingDeliveries,
        todayCollections,
      }));
    });

    const portersQuery = query(collection(db, 'porters'));
    const unsubscribePorters = onSnapshot(portersQuery, (snapshot) => {
      const portersList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setPorters(portersList);

      const onlineCount = portersList.filter(p => p.isOnline).length;
      const pendingDeposits = portersList.reduce((sum, p) => 
        sum + (p.stats?.pendingDeposits || 0), 0
      );

      setStats(prev => ({
        ...prev,
        totalPorters: snapshot.size,
        onlinePorters: onlineCount,
        pendingDeposits,
      }));

      setLoading(false);
    });

    return () => {
      unsubscribeTasks();
      unsubscribePorters();
    };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 bg-gray-50 min-h-screen">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-500 mt-1">Welcome back! Here's your delivery overview.</p>
        </div>
        <Link
          to="/spares-admin/create-task"
          className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-medium rounded-xl transition-all shadow-md"
        >
          + Create Task
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Deliveries"
          value={stats.totalDeliveries}
          icon={TruckIcon}
          color="text-red-600"
          bgColor="bg-red-100"
          trend="up"
          trendValue="+12% this week"
        />
        <StatCard
          title="Pending Deliveries"
          value={stats.pendingDeliveries}
          icon={ClockIcon}
          color="text-yellow-600"
          bgColor="bg-yellow-100"
        />
        <StatCard
          title="Online Porters"
          value={`${stats.onlinePorters}/${stats.totalPorters}`}
          icon={UsersIcon}
          color="text-green-600"
          bgColor="bg-green-100"
        />
        <StatCard
          title="Pending Deposits"
          value={`₹${stats.pendingDeposits.toLocaleString('en-IN')}`}
          icon={BanknotesIcon}
          color="text-blue-600"
          bgColor="bg-blue-100"
        />
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-bold text-gray-900">Recent Tasks</h2>
            <Link to="/spares-admin/tasks" className="text-red-600 hover:text-red-700 text-sm font-medium">
              View All →
            </Link>
          </div>
          <div className="space-y-4">
            {recentTasks.length > 0 ? (
              recentTasks.map(task => (
                <RecentTaskCard key={task.id} task={task} />
              ))
            ) : (
              <div className="text-center py-8">
                <TruckIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">No tasks yet</p>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-bold text-gray-900">Porter Status</h2>
            <Link to="/spares-admin/porters" className="text-red-600 hover:text-red-700 text-sm font-medium">
              Manage →
            </Link>
          </div>
          <div className="space-y-4">
            {porters.length > 0 ? (
              porters.slice(0, 5).map(porter => (
                <PorterStatusCard key={porter.id} porter={porter} />
              ))
            ) : (
              <div className="text-center py-8">
                <UsersIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">No porters added yet</p>
                <Link
                  to="/spares-admin/porters"
                  className="inline-block mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Add Porter
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Today's Summary */}
      <div className="bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-2xl p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">Today's Summary</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <p className="text-gray-500 text-sm">Collections</p>
            <p className="text-2xl font-bold text-gray-900">₹{stats.todayCollections.toLocaleString('en-IN')}</p>
          </div>
          <div>
            <p className="text-gray-500 text-sm">Completed</p>
            <p className="text-2xl font-bold text-green-600">
              {recentTasks.filter(t => t.status === 'delivered').length}
            </p>
          </div>
          <div>
            <p className="text-gray-500 text-sm">In Progress</p>
            <p className="text-2xl font-bold text-blue-600">
              {recentTasks.filter(t => ['accepted', 'picked_up', 'in_transit'].includes(t.status)).length}
            </p>
          </div>
          <div>
            <p className="text-gray-500 text-sm">Pending</p>
            <p className="text-2xl font-bold text-yellow-600">
              {recentTasks.filter(t => t.status === 'pending').length}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SparesDashboard;